/**
 * ============================================================================
 * ProcessRoute 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 ProcessRoute（工艺路线）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createProcessRouteApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createProcessRouteApi } from '@bayes-mes/shared/api/process-route/factory'
 * export const processRouteApi = createProcessRouteApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await processRouteApi.getProcessRouteList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createProcessRouteApi } from '@bayes-mes/shared/api/process-route/factory'
 * export const processRouteApi = createProcessRouteApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/process-route/factory
 * @description ProcessRoute 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 和业务方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  ProcessRoute,                         // 工艺路线类型
  ProcessRouteQueryParams,              // 列表查询参数
  ProcessRouteAllQueryParams,           // 获取所有工艺路线查询参数（不分页）
  CreateProcessRouteForm,               // 创建表单
  UpdateProcessRouteForm,               // 更新表单
  ApproveProcessRouteForm,              // 审核表单
  DuplicateProcessRouteForm,            // 复制表单
  BatchDeleteProcessRouteForm,          // 批量删除表单
  ImportProcessRoutesForm,              // 批量导入表单
  ImportProcessRoutesResult,            // 批量导入结果
  ProcessRouteStatistics,               // 单个工艺路线统计
  ProcessRouteGlobalStatistics,         // 全局统计
} from '../../types/process-route'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入请求客户端接口 =====
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 工厂函数定义 =====
/**
 * 创建 ProcessRoute 模块 API
 * 
 * @description
 * 工厂函数，接收一个请求客户端，返回 ProcessRoute 模块的所有 API 方法。
 * 
 * 🎯 设计目标：
 * 1. **依赖注入**：通过参数传入请求客户端，而不是直接依赖具体实现
 * 2. **平台无关**：Web 端和 App 端提供不同的客户端即可，API 方法完全相同
 * 3. **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * 4. **易于测试**：可以传入 mock 客户端进行单元测试
 * 
 * 💡 为什么使用工厂模式？
 * - Web 端使用 axios，App 端使用 uni.request，但它们的接口不同
 * - 我们不想为每个平台写一套 API 代码
 * - 通过工厂模式，只需提供一个符合 IRequestClient 接口的适配器即可
 * 
 * 📖 使用示例：
 * ```typescript
 * // Web 端
 * import { requestClient } from '#/api/request'
 * const webAdapter = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   // ...
 * }
 * const api = createProcessRouteApi(webAdapter)
 * 
 * // App 端
 * const uniAdapter = {
 *   get: (url, config) => uni.request({ url, method: 'GET', ... }),
 *   // ...
 * }
 * const api = createProcessRouteApi(uniAdapter)
 * 
 * // 使用方式完全相同
 * await api.getProcessRouteList({ page: 1 })
 * ```
 * 
 * @param client 请求客户端（符合 IRequestClient 接口）
 * @returns ProcessRoute 模块的所有 API 方法
 * 
 * @example
 * ```typescript
 * const api = createProcessRouteApi(myRequestClient)
 * const routes = await api.getProcessRouteList({ page: 1 })
 * ```
 */
export function createProcessRouteApi(client: IRequestClient) {
  return {
    // ========================================
    // 查询相关 API
    // ========================================

    /**
     * 获取工艺路线列表（分页）
     * 
     * @description
     * 获取分页的工艺路线列表，支持多种筛选条件。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.getProcessRouteList(params)
     * 2. 发送 GET 请求：/api/v1/process-routes?page=1&...
     * 3. 后端处理：ProcessRouteController::index()
     * 4. 返回数据：PaginationResult<ProcessRoute>
     * 
     * 📝 使用场景：
     * - 工艺路线列表页面
     * - 支持按物料、路线编码、名称、类型、状态等筛选
     * - 支持排序和分页
     * 
     * 💡 提示：
     * - 默认每页 20 条
     * - 默认按创建时间倒序
     * - 支持关联加载物料和工序信息（后端自动处理）
     * 
     * @param params 查询参数
     * @param params.material_id 物料ID（精确匹配）
     * @param params.material_code 物料编码（模糊匹配）
     * @param params.route_code 工艺路线编码（模糊匹配）
     * @param params.route_name 工艺路线名称（模糊匹配）
     * @param params.route_type 路线类型（STANDARD/ALTERNATIVE/TRIAL/OBSOLETE）
     * @param params.status 状态（DRAFT/ACTIVE/FROZEN/OBSOLETE）
     * @param params.is_default 是否只显示默认路线
     * @param params.sort_by 排序字段（默认：created_at）
     * @param params.sort_order 排序方向（asc/desc，默认：desc）
     * @param params.per_page 每页数量（默认：20）
     * @param params.page 当前页码（默认：1）
     * 
     * @returns 分页的工艺路线列表
     * 
     * @backend
     * - Controller: ProcessRouteController::index() (line 42)
     * - Service: ProcessRouteService::index() (line 26)
     * - Route: GET /api/v1/process-routes (line 430)
     * 
     * @example
     * ```typescript
     * // 获取第一页
     * const result = await api.getProcessRouteList({ page: 1 })
     * 
     * // 按物料筛选
     * const result = await api.getProcessRouteList({
     *   material_id: 1,
     *   status: 'ACTIVE'
     * })
     * 
     * // 模糊搜索
     * const result = await api.getProcessRouteList({
     *   route_name: '标准'
     * })
     * ```
     */
    getProcessRouteList: (params?: ProcessRouteQueryParams) =>
      client.get<PaginationResult<ProcessRoute>>(API_ROUTES.processRoute.list, { params }),

    /**
     * 获取所有工艺路线（不分页）
     * 
     * @description
     * 获取所有工艺路线，不分页返回，适用于下拉选择等场景。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.getAllProcessRoutes(params)
     * 2. 发送 GET 请求：/api/v1/process-routes/all
     * 3. 后端处理：ProcessRouteController::all()
     * 4. 返回数据：ProcessRoute[]
     * 
     * 📝 使用场景：
     * - 工艺路线下拉选择器
     * - 需要完整工艺路线列表的场景
     * 
     * ⚠️ 注意：
     * - 仅支持 material_id 和 status 两个筛选条件
     * - 数据量大时可能影响性能，建议限制使用场景
     * 
     * @param params 查询参数
     * @param params.material_id 物料ID（精确匹配）
     * @param params.status 状态（精确匹配）
     * 
     * @returns 工艺路线列表
     * 
     * @backend
     * - Controller: ProcessRouteController::all() (line 72)
     * - Service: ProcessRouteService::all() (line 79)
     * - Route: GET /api/v1/process-routes/all (line 432)
     * 
     * @example
     * ```typescript
     * // 获取所有工艺路线
     * const routes = await api.getAllProcessRoutes()
     * 
     * // 获取指定物料的所有工艺路线
     * const routes = await api.getAllProcessRoutes({ material_id: 1 })
     * 
     * // 获取所有生效状态的工艺路线
     * const routes = await api.getAllProcessRoutes({ status: 'ACTIVE' })
     * ```
     */
    getAllProcessRoutes: (params?: ProcessRouteAllQueryParams) =>
      client.get<ProcessRoute[]>(API_ROUTES.processRoute.all, { params }),

    /**
     * 获取工艺路线详情
     * 
     * @description
     * 根据 ID 获取工艺路线的详细信息，包括关联的物料和工序列表。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.getProcessRouteDetail(id)
     * 2. 发送 GET 请求：/api/v1/process-routes/{id}
     * 3. 后端处理：ProcessRouteController::show()
     * 4. 返回数据：ProcessRoute（包含 material 和 operations）
     * 
     * 📝 使用场景：
     * - 工艺路线详情页面
     * - 编辑工艺路线前查看现有数据
     * 
     * 💡 提示：
     * - 返回的数据包含关联的物料信息（material 字段）
     * - 返回的数据包含工序列表（operations 字段）
     * - 工序按序号排序（operation_seq）
     * 
     * @param id 工艺路线ID
     * 
     * @returns 工艺路线详情
     * 
     * @backend
     * - Controller: ProcessRouteController::show() (line 127)
     * - Service: ProcessRouteService::show() (line 101)
     * - Route: GET /api/v1/process-routes/{id} (line 437)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const route = await api.getProcessRouteDetail(1)
     * console.log(route.routeName)
     * console.log(route.material?.materialName)
     * console.log(route.operations?.length)
     * ```
     */
    getProcessRouteDetail: (id: number) =>
      client.get<ProcessRoute>(API_ROUTES.processRoute.processRoutes(id)),

    /**
     * 根据工艺路线编码查询
     * 
     * @description
     * 通过工艺路线编码查找工艺路线，编码必须精确匹配。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.findProcessRouteByCode(code)
     * 2. 发送 GET 请求：/api/v1/process-routes/code/{code}
     * 3. 后端处理：ProcessRouteController::findByCode()
     * 4. 返回数据：ProcessRoute
     * 
     * 📝 使用场景：
     * - 通过扫描条码快速查找工艺路线
     * - 根据编码验证工艺路线是否存在
     * - 导入数据时根据编码匹配工艺路线
     * 
     * 💡 提示：
     * - 工艺路线编码必须唯一
     * - 返回的数据包含关联的物料和工序
     * 
     * @param code 工艺路线编码
     * 
     * @returns 工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::findByCode() (line 223)
     * - Service: ProcessRouteService::findByCode() (line 256)
     * - Route: GET /api/v1/process-routes/code/{code} (line 433)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const route = await api.findProcessRouteByCode('RT-001')
     * console.log(route.routeName)
     * ```
     */
    findProcessRouteByCode: (code: string) =>
      client.get<ProcessRoute>(API_ROUTES.processRoute.findByCode(code)),

    /**
     * 获取物料的默认工艺路线
     * 
     * @description
     * 获取指定物料的默认工艺路线（同一物料只能有一个默认路线）。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.getDefaultRoute(materialId)
     * 2. 发送 GET 请求：/api/v1/process-routes/material/{materialId}/default
     * 3. 后端处理：ProcessRouteController::getDefaultRoute()
     * 4. 返回数据：ProcessRoute（is_default=true 且 status=ACTIVE）
     * 
     * 📝 使用场景：
     * - 创建生产工单时自动选择默认工艺路线
     * - 物料详情页显示默认工艺路线
     * - 快速查看物料的标准生产流程
     * 
     * 💡 业务规则：
     * - 同一物料只能有一个默认工艺路线
     * - 只返回生效状态（ACTIVE）的默认路线
     * - 如果没有默认路线，返回 404
     * 
     * @param materialId 物料ID
     * 
     * @returns 默认工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::getDefaultRoute() (line 244)
     * - Service: ProcessRouteService::getDefaultRoute() (line 269)
     * - Route: GET /api/v1/process-routes/material/{materialId}/default (line 434)
     * 
     * @throws 404 - 该物料没有默认工艺路线
     * 
     * @example
     * ```typescript
     * try {
     *   const defaultRoute = await api.getDefaultRoute(1)
     *   console.log('默认工艺路线:', defaultRoute.routeName)
     * } catch (error) {
     *   console.log('该物料没有默认工艺路线')
     * }
     * ```
     */
    getDefaultRoute: (materialId: number) =>
      client.get<ProcessRoute>(API_ROUTES.processRoute.detail(materialId)),

    // ========================================
    // 创建和更新相关 API
    // ========================================

    /**
     * 创建工艺路线
     * 
     * @description
     * 创建新的工艺路线，支持设置为默认路线。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.createProcessRoute(data)
     * 2. 发送 POST 请求：/api/v1/process-routes
     * 3. 后端处理：ProcessRouteController::store()
     * 4. 返回数据：新创建的 ProcessRoute
     * 
     * 📝 使用场景：
     * - 新增工艺路线
     * - 为新产品配置生产流程
     * 
     * 💡 业务规则：
     * - 工艺路线编码必须唯一
     * - 如果设置为默认（is_default=true），会自动取消同一物料的其他默认路线
     * - 创建后记录审计日志
     * - 返回的数据包含关联的物料和工序
     * 
     * ⚠️ 字段命名：
     * - 请求数据使用 snake_case（如 route_code）
     * - 响应数据使用 camelCase（如 routeCode）
     * 
     * @param data 工艺路线表单数据
     * @param data.route_code 工艺路线编码（必填，最大50字符，唯一）
     * @param data.route_name 工艺路线名称（必填，最大100字符）
     * @param data.version 版本号（可选，最大20字符）
     * @param data.material_id 关联物料ID（必填）
     * @param data.material_code 物料编码（必填，最大50字符）
     * @param data.material_name 物料名称（必填，最大100字符）
     * @param data.route_type 路线类型（可选，枚举值）
     * @param data.is_default 是否默认工艺路线（可选）
     * @param data.status 状态（可选，枚举值）
     * @param data.effective_date 生效日期（可选）
     * @param data.expiry_date 失效日期（可选）
     * @param data.description 描述（可选）
     * @param data.remark 备注（可选）
     * 
     * @returns 创建的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::store() (line 90)
     * - Service: ProcessRouteService::create() (line 119)
     * - Route: POST /api/v1/process-routes (line 435)
     * 
     * @throws 400 - 工艺路线编码已存在
     * @throws 422 - 表单验证失败
     * 
     * @example
     * ```typescript
     * const route = await api.createProcessRoute({
     *   route_code: 'RT-001',
     *   route_name: '标准生产工艺',
     *   version: 'V1.0',
     *   material_id: 1,
     *   material_code: 'MAT-001',
     *   material_name: '产品A',
     *   route_type: 'STANDARD',
     *   is_default: true,
     *   status: 'DRAFT'
     * })
     * ```
     */
    createProcessRoute: (data: CreateProcessRouteForm) =>
      client.post<ProcessRoute>(API_ROUTES.processRoute.create, data),

    /**
     * 更新工艺路线
     * 
     * @description
     * 更新工艺路线的基本信息，所有字段都是可选的。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.updateProcessRoute(id, data)
     * 2. 发送 PUT 请求：/api/v1/process-routes/{id}
     * 3. 后端处理：ProcessRouteController::update()
     * 4. 返回数据：更新后的 ProcessRoute
     * 
     * 📝 使用场景：
     * - 修改工艺路线信息
     * - 更新版本号
     * - 调整路线类型
     * 
     * 💡 业务规则：
     * - 只更新提供的字段
     * - 如果修改工艺路线编码，会检查新编码的唯一性
     * - 如果设置为默认，会取消同一物料的其他默认路线
     * - 返回的数据包含关联的物料和工序
     * 
     * ⚠️ 字段命名：
     * - 请求数据使用 snake_case
     * - 响应数据使用 camelCase
     * 
     * @param id 工艺路线ID
     * @param data 更新数据（所有字段都是可选的）
     * 
     * @returns 更新后的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::update() (line 144)
     * - Service: ProcessRouteService::update() (line 156)
     * - Route: PUT /api/v1/process-routes/{id} (line 439)
     * 
     * @throws 400 - 工艺路线编码已存在
     * @throws 404 - 工艺路线不存在
     * @throws 422 - 表单验证失败
     * 
     * @example
     * ```typescript
     * // 只更新名称
     * await api.updateProcessRoute(1, {
     *   route_name: '新的工艺路线名称'
     * })
     * 
     * // 更新多个字段
     * await api.updateProcessRoute(1, {
     *   route_name: '标准工艺V2',
     *   version: 'V2.0',
     *   description: '优化后的工艺流程'
     * })
     * ```
     */
    updateProcessRoute: (id: number, data: UpdateProcessRouteForm) =>
      client.put<ProcessRoute>(API_ROUTES.processRoute.processRoutes(id), data),

    /**
     * 删除工艺路线
     * 
     * @description
     * 删除指定的工艺路线（软删除），同时删除所有关联的工序。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.deleteProcessRoute(id)
     * 2. 发送 DELETE 请求：/api/v1/process-routes/{id}
     * 3. 后端处理：ProcessRouteController::destroy()
     * 4. 返回：操作成功
     * 
     * 📝 使用场景：
     * - 删除错误创建的工艺路线
     * - 清理过时的工艺路线
     * 
     * 💡 业务规则：
     * - 软删除（可恢复）
     * - 自动删除关联的所有工序
     * - 记录审计日志
     * - 如果有关联的工单，无法删除（后续开发工单模块时添加）
     * 
     * ⚠️ 警告：
     * - 删除后不能恢复（业务层面）
     * - 关联的工序也会被删除
     * 
     * @param id 工艺路线ID
     * 
     * @returns 操作结果
     * 
     * @backend
     * - Controller: ProcessRouteController::destroy() (line 181)
     * - Service: ProcessRouteService::destroy() (line 194)
     * - Route: DELETE /api/v1/process-routes/{id} (line 440)
     * 
     * @throws 400 - 该工艺路线已被工单使用，无法删除
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * await api.deleteProcessRoute(1)
     * console.log('删除成功')
     * ```
     */
    deleteProcessRoute: (id: number) =>
      client.delete<void>(API_ROUTES.processRoute.delete(id)),

    /**
     * 批量删除工艺路线
     * 
     * @description
     * 批量删除多个工艺路线，返回成功删除的数量。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.batchDeleteProcessRoute({ ids: [1, 2, 3] })
     * 2. 发送 POST 请求：/api/v1/process-routes/batch-delete
     * 3. 后端处理：ProcessRouteController::batchDestroy()
     * 4. 返回数据：{ count: 成功删除的数量 }
     * 
     * 📝 使用场景：
     * - 工艺路线列表页批量删除
     * - 清理测试数据
     * 
     * 💡 业务规则：
     * - 逐个删除，跳过删除失败的项
     * - 返回成功删除的数量
     * - 所有操作在事务中执行
     * 
     * ⚠️ 字段命名：
     * - 请求数据使用 snake_case（ids）
     * - 响应数据使用 camelCase（count）
     * 
     * @param data 批量删除表单
     * @param data.ids 工艺路线ID列表
     * 
     * @returns 操作结果，包含成功删除的数量
     * 
     * @backend
     * - Controller: ProcessRouteController::batchDestroy() (line 197)
     * - Service: ProcessRouteService::batchDestroy() (line 230)
     * - Route: POST /api/v1/process-routes/batch-delete (line 436)
     * 
     * @example
     * ```typescript
     * const result = await api.batchDeleteProcessRoute({
     *   ids: [1, 2, 3]
     * })
     * console.log(`成功删除 ${result.count} 条工艺路线`)
     * ```
     */
    batchDeleteProcessRoute: (data: BatchDeleteProcessRouteForm) =>
      client.post<{ count: number }>(API_ROUTES.processRoute.batchDelete, data),

    /**
     * 批量导入工艺路线
     * 
     * @description
     * 批量导入工艺路线数据，前端负责 Excel 解析，后端只接收解析后的数据进行导入。
     * 支持同时导入工艺路线及其关联的工序数据。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.importProcessRoutes({ routes: [...] })
     * 2. 发送 POST 请求：/api/v1/process-routes/import
     * 3. 后端处理：ProcessRouteController::batchImport()
     * 4. 返回数据：ImportProcessRoutesResult
     * 
     * 📝 使用场景：
     * - 从 Excel 批量导入工艺路线
     * - 数据迁移
     * - 初始化基础数据
     * 
     * 💡 业务规则：
     * - 物料编码必须存在于系统中
     * - 工艺路线编码不能重复（重复的会跳过并记录错误）
     * - 支持同时导入工序（operations 字段）
     * - 导入的工艺路线状态为草稿（DRAFT）
     * 
     * @param data 导入表单
     * @param data.routes 工艺路线数据数组
     * 
     * @returns 导入结果，包含成功数、失败数、错误详情
     * 
     * @backend
     * - Controller: ProcessRouteController::batchImport()
     * - Service: ProcessRouteService::batchImport()
     * - Route: POST /api/v1/process-routes/import
     * 
     * @example
     * ```typescript
     * const result = await api.importProcessRoutes({
     *   routes: [
     *     {
     *       route_code: 'RT-001',
     *       route_name: '标准工艺路线',
     *       material_code: 'MAT-001',
     *       version: 'V1.0',
     *       operations: [
     *         { operation_code: 'OP-001', operation_name: '下料', sequence: 1 },
     *         { operation_code: 'OP-002', operation_name: '加工', sequence: 2 }
     *       ]
     *     }
     *   ]
     * })
     * console.log(`成功: ${result.success_count}, 失败: ${result.failed_count}`)
     * ```
     */
    importProcessRoutes: (data: ImportProcessRoutesForm) =>
      client.post<ImportProcessRoutesResult>(API_ROUTES.processRoute.import, data),

    // ========================================
    // 业务操作相关 API
    // ========================================

    /**
     * 设置为默认工艺路线
     * 
     * @description
     * 将指定工艺路线设置为默认路线，同时取消同一物料的其他默认路线。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.setAsDefaultRoute(id)
     * 2. 发送 PUT 请求：/api/v1/process-routes/{id}/set-default
     * 3. 后端处理：ProcessRouteController::setAsDefault()
     * 4. 返回数据：更新后的 ProcessRoute（is_default=true）
     * 
     * 📝 使用场景：
     * - 切换物料的默认工艺路线
     * - 新增工艺路线后设置为默认
     * 
     * 💡 业务规则：
     * - 同一物料只能有一个默认工艺路线
     * - 自动取消同一物料的其他默认路线
     * - 设置前会验证工艺路线状态
     * 
     * @param id 工艺路线ID
     * 
     * @returns 更新后的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::setAsDefault() (line 265)
     * - Service: ProcessRouteService::setAsDefault() (line 285)
     * - Route: PUT /api/v1/process-routes/{id}/set-default (line 441)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const route = await api.setAsDefaultRoute(1)
     * console.log(route.isDefault) // true
     * ```
     */
    setAsDefaultRoute: (id: number) =>
      client.put<ProcessRoute>(API_ROUTES.processRoute.setDefault(id)),

    /**
     * 审核工艺路线
     * 
     * @description
     * 审核通过草稿状态的工艺路线，审核后状态变为生效（ACTIVE）。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.approveProcessRoute(id, data)
     * 2. 发送 PUT 请求：/api/v1/process-routes/{id}/approve
     * 3. 后端处理：ProcessRouteController::approve()
     * 4. 返回数据：更新后的 ProcessRoute（status=ACTIVE）
     * 
     * 📝 使用场景：
     * - 工艺路线审核流程
     * - 确认工艺路线可以投入使用
     * 
     * 💡 业务规则：
     * - 只有草稿状态（DRAFT）的工艺路线可以审核
     * - 审核后状态变为生效（ACTIVE）
     * - 记录审核人和审核时间
     * - 设置生效日期为当前时间
     * 
     * ⚠️ 字段命名：
     * - 请求数据使用 snake_case
     * - 响应数据使用 camelCase
     * 
     * @param id 工艺路线ID
     * @param data 审核表单
     * @param data.approved_by 审核人ID
     * @param data.approved_by_name 审核人姓名
     * 
     * @returns 更新后的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::approve() (line 282)
     * - Service: ProcessRouteService::approve() (line 310)
     * - Route: PUT /api/v1/process-routes/{id}/approve (line 442)
     * 
     * @throws 400 - 只有草稿状态的工艺路线才能审核
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const route = await api.approveProcessRoute(1, {
     *   approved_by: 1,
     *   approved_by_name: '张三'
     * })
     * console.log(route.status) // 'ACTIVE'
     * console.log(route.approvedByName) // '张三'
     * ```
     */
    approveProcessRoute: (id: number, data: ApproveProcessRouteForm) =>
      client.put<ProcessRoute>(API_ROUTES.processRoute.approveApplication(id), data),

    /**
     * 冻结工艺路线
     * 
     * @description
     * 冻结工艺路线，冻结后暂时不可使用，但可以解冻。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.freezeProcessRoute(id)
     * 2. 发送 PUT 请求：/api/v1/process-routes/{id}/freeze
     * 3. 后端处理：ProcessRouteController::freeze()
     * 4. 返回数据：更新后的 ProcessRoute（status=FROZEN）
     * 
     * 📝 使用场景：
     * - 暂停使用某个工艺路线
     * - 等待优化改进的工艺路线
     * 
     * 💡 业务规则：
     * - 状态变更为冻结（FROZEN）
     * - 冻结后不能用于生产
     * - 可以通过更新接口解冻（修改 status）
     * 
     * @param id 工艺路线ID
     * 
     * @returns 更新后的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::freeze() (line 312)
     * - Service: ProcessRouteService::freeze() (line 338)
     * - Route: PUT /api/v1/process-routes/{id}/freeze (line 443)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const route = await api.freezeProcessRoute(1)
     * console.log(route.status) // 'FROZEN'
     * ```
     */
    freezeProcessRoute: (id: number) =>
      client.put<ProcessRoute>(API_ROUTES.processRoute.freeze(id)),

    /**
     * 淘汰工艺路线
     * 
     * @description
     * 淘汰工艺路线，标记为过时不再使用。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.obsoleteProcessRoute(id)
     * 2. 发送 PUT 请求：/api/v1/process-routes/{id}/obsolete
     * 3. 后端处理：ProcessRouteController::obsolete()
     * 4. 返回数据：更新后的 ProcessRoute（status=OBSOLETE, route_type=OBSOLETE）
     * 
     * 📝 使用场景：
     * - 工艺路线已被新版本替代
     * - 产品停产，工艺路线不再使用
     * 
     * 💡 业务规则：
     * - 状态变更为淘汰（OBSOLETE）
     * - 路线类型也变更为淘汰（OBSOLETE）
     * - 失效日期设置为当前时间
     * - 淘汰后不能再使用
     * 
     * @param id 工艺路线ID
     * 
     * @returns 更新后的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::obsolete() (line 328)
     * - Service: ProcessRouteService::obsolete() (line 361)
     * - Route: PUT /api/v1/process-routes/{id}/obsolete (line 444)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const route = await api.obsoleteProcessRoute(1)
     * console.log(route.status) // 'OBSOLETE'
     * console.log(route.routeType) // 'OBSOLETE'
     * ```
     */
    obsoleteProcessRoute: (id: number) =>
      client.put<ProcessRoute>(API_ROUTES.processRoute.obsolete(id)),

    /**
     * 复制工艺路线（创建新版本）
     * 
     * @description
     * 复制现有工艺路线并创建新版本，包括所有工序。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.duplicateProcessRoute(id, data)
     * 2. 发送 POST 请求：/api/v1/process-routes/{id}/duplicate
     * 3. 后端处理：ProcessRouteController::duplicate()
     * 4. 返回数据：新创建的 ProcessRoute（status=DRAFT）
     * 
     * 📝 使用场景：
     * - 基于现有工艺路线创建新版本
     * - 为不同物料复制相似的工艺路线
     * 
     * 💡 业务规则：
     * - 复制所有基本信息和工序
     * - 新路线状态为草稿（DRAFT）
     * - 新路线不是默认路线（is_default=false）
     * - 清空审核信息和生效/失效日期
     * - 如果提供新编码，使用新编码；否则自动生成（原编码-V版本号）
     * 
     * ⚠️ 字段命名：
     * - 请求数据使用 snake_case
     * - 响应数据使用 camelCase
     * 
     * @param id 要复制的工艺路线ID
     * @param data 复制表单
     * @param data.new_version 新版本号（必填）
     * @param data.new_route_code 新路线编码（可选）
     * 
     * @returns 新创建的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::duplicate() (line 345)
     * - Service: ProcessRouteService::duplicate() (line 386)
     * - Route: POST /api/v1/process-routes/{id}/duplicate (line 445)
     * 
     * @throws 400 - 新路线编码已存在
     * @throws 404 - 原工艺路线不存在
     * 
     * @example
     * ```typescript
     * // 使用自动生成的编码
     * const newRoute = await api.duplicateProcessRoute(1, {
     *   new_version: 'V2.0'
     * })
     * // 如果原编码是 RT-001，新编码将是 RT-001-V2.0
     * 
     * // 使用指定的编码
     * const newRoute = await api.duplicateProcessRoute(1, {
     *   new_version: 'V2.0',
     *   new_route_code: 'RT-002'
     * })
     * ```
     */
    duplicateProcessRoute: (id: number, data: DuplicateProcessRouteForm) =>
      client.post<ProcessRoute>(API_ROUTES.processRoute.duplicate(id), data),

    /**
     * 重新计算工艺路线的总工时和工序数量
     * 
     * @description
     * 当手动修改工序后，调用此接口重新计算工艺路线的总工时和工序数量。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.recalculateProcessRoute(id)
     * 2. 发送 POST 请求：/api/v1/process-routes/{id}/recalculate
     * 3. 后端处理：ProcessRouteController::recalculate()
     * 4. 返回数据：更新后的 ProcessRoute
     * 
     * 📝 使用场景：
     * - 修改工序后同步总工时
     * - 增删工序后更新工序数量
     * - 数据修复和校准
     * 
     * 💡 计算规则：
     * - 总工时 = 所有工序的标准工时之和
     * - 工序数量 = 工序列表的数量
     * 
     * @param id 工艺路线ID
     * 
     * @returns 更新后的工艺路线信息
     * 
     * @backend
     * - Controller: ProcessRouteController::recalculate() (line 375)
     * - Service: ProcessRouteService::recalculate() (line 415)
     * - Route: POST /api/v1/process-routes/{id}/recalculate (line 446)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * // 修改工序后重新计算
     * await api.updateOperation(operationId, { standard_time: 2.5 })
     * const route = await api.recalculateProcessRoute(routeId)
     * console.log(route.totalStandardTime)
     * console.log(route.operationCount)
     * ```
     */
    recalculateProcessRoute: (id: number) =>
      client.post<ProcessRoute>(API_ROUTES.processRoute.recalculate(id)),

    // ========================================
    // 统计相关 API
    // ========================================

    /**
     * 获取工艺路线全局统计信息
     * 
     * @description
     * 获取所有工艺路线的全局统计信息，用于Dashboard展示。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.getProcessRouteStatistics()
     * 2. 发送 GET 请求：/api/v1/process-routes/statistics
     * 3. 后端处理：ProcessRouteController::statistics()
     * 4. 返回数据：ProcessRouteGlobalStatistics
     * 
     * 📝 使用场景：
     * - Dashboard 工艺路线概览
     * - 工艺路线管理首页
     * 
     * 💡 统计内容：
     * - 总数量和各状态数量
     * - 按状态和类型的分组统计
     * - 默认路线数量
     * - 有/无工序的路线数量
     * - 平均工序数和工时统计
     * 
     * @returns 全局统计信息
     * 
     * @backend
     * - Controller: ProcessRouteController::statistics() (line 390)
     * - Service: ProcessRouteService::getGlobalStatistics() (line 477)
     * - Route: GET /api/v1/process-routes/statistics (line 431)
     * 
     * @example
     * ```typescript
     * const stats = await api.getProcessRouteStatistics()
     * console.log('工艺路线总数:', stats.totalRoutes)
     * console.log('生效中:', stats.activeCount)
     * console.log('草稿:', stats.draftCount)
     * console.log('平均工序数:', stats.avgOperationsPerRoute)
     * ```
     */
    getProcessRouteStatistics: () =>
      client.get<ProcessRouteGlobalStatistics>(API_ROUTES.processRoute.statistics),

    /**
     * 获取单个工艺路线统计信息
     * 
     * @description
     * 获取指定工艺路线的详细统计信息，包括工序统计。
     * 
     * 🔄 数据流向：
     * 1. 前端调用：api.getProcessRouteDetailStatistics(id)
     * 2. 发送 GET 请求：/api/v1/process-routes/{id}/statistics
     * 3. 后端处理：ProcessRouteController::routeStatistics()
     * 4. 返回数据：ProcessRouteStatistics
     * 
     * 📝 使用场景：
     * - 工艺路线详情页的统计展示
     * - 分析工艺路线的工时构成
     * 
     * 💡 统计内容：
     * - 基本信息（编码、名称、物料）
     * - 工序数量
     * - 各类工时统计（标准、准备、等待、排队）
     * - 特殊工序统计（检验、并行、可选）
     * 
     * @param id 工艺路线ID
     * 
     * @returns 工艺路线统计信息
     * 
     * @backend
     * - Controller: ProcessRouteController::routeStatistics() (line 406)
     * - Service: ProcessRouteService::statistics() (line 449)
     * - Route: GET /api/v1/process-routes/{id}/statistics (line 438)
     * 
     * @throws 404 - 工艺路线不存在
     * 
     * @example
     * ```typescript
     * const stats = await api.getProcessRouteDetailStatistics(1)
     * console.log('工序数量:', stats.operationCount)
     * console.log('总标准工时:', stats.totalStandardTime)
     * console.log('需要检验的工序:', stats.inspectionOperationCount)
     * ```
     */
    getProcessRouteDetailStatistics: (id: number) =>
      client.get<ProcessRouteStatistics>(API_ROUTES.processRoute.processRoutesStatistics(id)),
  }
}

